import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { format } from 'date-fns';

interface LineChartProps {
  data: any[];
  title?: string;
  lines: { dataKey: string; stroke: string; name: string }[];
  xAxisKey: string;
  showGrid?: boolean;
  xAxisCount?: number;
  formatDate?: boolean;
  dateInterval?: number;
  onPointClick?: (date: string, dataPoint: any) => void;
}

export const DashboardLineChart: React.FC<LineChartProps> = ({
  data,
  title = "Trends over Months",
  lines,
  xAxisKey,
  showGrid = false,
  xAxisCount,
  formatDate = false,
  dateInterval = 15,
  onPointClick
}) => {
  if (!data || data.length === 0) {
    return <div className="text-center p-4">No data available</div>;
  }

  // Calculate appropriate interval based on data length
  const calculateInterval = () => {
    const dataLength = data.length;
    if (dataLength <= 15) return 0; // Show all dates
    if (dataLength <= 30) return 1; // Show every other date
    if (dataLength <= 60) return 2; // Show every third date
    if (dataLength <= 90) return 3; // Show every fourth date
    return 4; // Show every fifth date for larger datasets
  };

  const formatDateLabel = (value: string) => {
    if (formatDate) {
      try {
        return format(new Date(value), 'MMM d');
      } catch (e) {
        return value;
      }
    }
    return value;
  };

  return (
    <div>
      {/* {title && <h3 className="text-lg font-bold mb-4 ml-4">{title}</h3>} */}
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data} 
            margin={{ top: 5, right: 30, left: 20, bottom: 25 }}
            onClick={(e: any) => {
              // Use the onClick on the chart to get active data
              if (e && e.activePayload && e.activePayload.length > 0) {
                // The payload property in activePayload[0] should contain the actual data point
                const clickedDataPoint = e.activePayload[0].payload;
                console.log('LineChart onClick - clickedDataPoint:', clickedDataPoint);
                if (clickedDataPoint && clickedDataPoint[xAxisKey]) {
                  onPointClick?.(clickedDataPoint[xAxisKey], clickedDataPoint);
                } else {
                  console.warn('LineChart onClick - Invalid dataPoint or xAxisKey', clickedDataPoint);
                }
              } else {
                console.warn('LineChart onClick - No active payload found', e);
              }
            }}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" />}
            <XAxis 
              dataKey={xAxisKey} 
              tick={{ 
                fontSize: 12,
                // Remove the rotation transform to make labels horizontal
              }}
              height={50}
              tickCount={xAxisCount}
              interval={formatDate ? calculateInterval() : 'preserveStartEnd'}
              tickFormatter={(value) => {
                if (formatDate) {
                  try {
                    return format(new Date(value), 'MMM d');
                  } catch (e) {
                    return value;
                  }
                }
                return value;
              }}
            />
            <YAxis />
            <Tooltip 
              labelFormatter={(label) => {
                if (formatDate) {
                  try {
                    return format(new Date(label), 'MMM d, yyyy');
                  } catch (e) {
                    return label;
                  }
                }
                return label;
              }}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-background border rounded-lg shadow-lg p-3">
                      <p className="font-medium">{formatDate ? formatDateLabel(label) : label}</p>
                      {payload.map((entry: any, index: number) => (
                        <p key={index} style={{ color: entry.color }}>
                          {entry.name}: {entry.value}
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            {lines.map((line, index) => (
              <Line
                key={index}
                type="monotone"
                dataKey={line.dataKey}
                stroke={line.stroke}
                name={line.name}
                strokeWidth={2}
                dot={{ strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
